import styled, { css } from 'styled-components';
import {
  brand,
  breakpoints,
  surface,
  touchTarget,
  textSize,
  titleType,
  tones,
} from '../../../styles/brand';

const accent = '#ff9800';
const ink = '#323232';
const muted = '#6b6b6b';
const line = '#e8e8e8';

export const Panel = styled.section`
  border: 1px solid ${line};
  border-radius: 1rem;
  background: #fff;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`;

export const PanelTitle = styled.div`
  h2 {
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(1.3)};
    ${titleType}
    margin: 0;
  }

  p {
    margin: 0.2rem 0 0;
    color: ${muted};
    font-size: ${textSize(0.85)};
  }
`;

export const Timer = styled.div<{
  $phase: 'presentation' | 'questions' | 'over';
}>`
  font-variant-numeric: tabular-nums;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.8rem 1rem;
  border-radius: 0.8rem;
  transition: background 200ms ease-out;
  background: ${({ $phase }) =>
    $phase === 'presentation'
      ? surface.page
      : $phase === 'questions'
        ? '#fff4d6'
        : tones.red.soft};
`;

export const TimerLabel = styled.div`
  font-size: ${textSize(0.8)};
  color: ${muted};

  strong {
    display: block;
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(1.9)};
    line-height: 1;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    color: ${ink};
    margin-top: 0.15rem;
  }
`;

export const TimerActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const SmallButton = styled.button<{ $primary?: boolean }>`
  border: 1px solid ${({ $primary }) => ($primary ? brand.orange : line)};
  background: ${({ $primary }) => ($primary ? brand.orange : brand.white)};
  color: ${({ $primary }) => ($primary ? brand.white : ink)};
  border-radius: 0.6rem;
  min-height: ${touchTarget};
  padding: 0.45rem 0.9rem;
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.85)};
  cursor: pointer;
  touch-action: manipulation;
  transition: transform 100ms ease-out;

  &:active {
    transform: scale(0.97);
  }
`;

export const Criterion = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.4rem 1rem;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid ${line};

  h3 {
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(0.95)};
    margin: 0;
  }

  ul {
    grid-column: 1 / -1;
    margin: 0;
    padding-left: 1.1rem;
    color: ${muted};
    font-size: ${textSize(0.8)};
    line-height: 1.45;
  }
`;

export const ScoreControl = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 0.9rem;

  /* Slider desenhado à mão: com accent-color o Chrome escurece o trilho
     para dar contraste ao laranja, e ele fica preto. Trilho claro, parte
     preenchida na cor de destaque (--fill vem do componente) e botão branco
     com sombra, que lê como objeto físico em cima do trilho. */
  input[type='range'] {
    flex: 1;
    height: ${touchTarget};
    margin: 0;
    background: transparent;
    cursor: pointer;
    touch-action: manipulation;
    -webkit-appearance: none;
    appearance: none;

    &::-webkit-slider-runnable-track {
      height: 6px;
      border-radius: 999px;
      background: linear-gradient(
        to right,
        ${accent} var(--fill, 0%),
        ${surface.line} var(--fill, 0%)
      );
    }

    &::-moz-range-track {
      height: 6px;
      border-radius: 999px;
      background: ${surface.line};
    }

    &::-moz-range-progress {
      height: 6px;
      border-radius: 999px;
      background: ${accent};
    }

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 1.5rem;
      height: 1.5rem;
      margin-top: calc(3px - 0.75rem);
      border: 0;
      border-radius: 50%;
      background: ${brand.white};
      box-shadow:
        0 1px 4px rgba(0, 0, 0, 0.22),
        0 0 0 0.5px rgba(0, 0, 0, 0.08);
    }

    &::-moz-range-thumb {
      width: 1.5rem;
      height: 1.5rem;
      border: 0;
      border-radius: 50%;
      background: ${brand.white};
      box-shadow:
        0 1px 4px rgba(0, 0, 0, 0.22),
        0 0 0 0.5px rgba(0, 0, 0, 0.08);
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
`;

export const ScoreBox = styled.input`
  width: 4rem;
  height: ${touchTarget};
  border: 1px solid ${line};
  /* 16px: o iOS não dá zoom na página ao focar o campo. */
  font-size: ${textSize(1)};
  border-radius: 0.55rem;
  text-align: center;
  font-family: 'RedHatDisplay-Bold';
  font-variant-numeric: tabular-nums;
  color: ${ink};
  outline: none;

  &:focus {
    border-color: ${accent};
    box-shadow: 0 0 0 3px rgba(255, 152, 0, 0.18);
  }
`;

export const Max = styled.span`
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.8)};
  font-variant-numeric: tabular-nums;
  color: ${muted};
  white-space: nowrap;
`;

export const Footer = styled.div<{ $sticky?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid ${line};

  /* O PrimaryButton não tem tamanho próprio: cada tela dá o seu (FE-72),
     com os mesmos valores dos botões da tela do torneio. */
  .primary-button {
    height: 2.8rem;
    padding: 0 1.4rem;
    border: 0;
    border-radius: 0.55rem;
    font-size: 0.95rem;
    gap: 0.4rem;
    box-shadow: 0px 3px 8px rgba(255, 152, 0, 0.3);
    cursor: pointer;
  }

  @media (max-width: ${breakpoints.md}) {
    .primary-button {
      flex: 1 1 100%;
      height: 3rem;
    }
  }

  /* Na folha do celular, a ação principal fica sempre à mão (FE-66). */
  ${({ $sticky }) =>
    $sticky &&
    css`
      position: sticky;
      bottom: 0;
      z-index: 1;
      margin: 0 -1.25rem -1.25rem;
      padding: 0.85rem 1.25rem calc(0.85rem + env(safe-area-inset-bottom));
      background: rgba(255, 255, 255, 0.92);
      backdrop-filter: blur(16px) saturate(180%);

      @media (prefers-reduced-transparency: reduce) {
        background: #fff;
        backdrop-filter: none;
      }
    `}
`;

/** Aviso dentro do formulário (desclassificada, erro ao salvar). */
export const Notice = styled.div<{ $tone: 'warning' | 'error' }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 0.75rem;
  padding: 0.7rem 0.9rem;
  border-radius: 0.7rem;
  font-size: ${textSize(0.85)};
  ${({ $tone }) =>
    $tone === 'error'
      ? css`
          color: ${tones.red.text};
          background: ${tones.red.soft};
        `
      : css`
          color: #8a6100;
          background: #fff4d6;
        `}
`;

export const Total = styled.div`
  font-variant-numeric: tabular-nums;
  font-size: ${textSize(0.85)};
  color: ${muted};

  strong {
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(2)};
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    color: ${ink};
    margin-right: 0.25rem;
  }
`;

export const Note = styled.p`
  margin: 0;
  font-size: ${textSize(0.8)};
  color: ${muted};
`;
